import React, { useState } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';

type ButtonSize = 'sm' | 'md' | 'lg';

interface ClaimButtonProps {
  assignmentId: string;
  onClaim?: (assignmentId: string) => Promise<void> | void;
  disabled?: boolean;
  isLoading?: boolean;
  size?: ButtonSize;
  brand?: BrandConfig;
}

const ClaimButton: React.FC<ClaimButtonProps> = ({
  assignmentId,
  onClaim,
  disabled = false,
  isLoading = false,
  size = 'md',
  brand,
}) => {
  const [confirming, setConfirming] = useState(false);

  const handleClick = async (): Promise<void> => {
    if (confirming) {
      // User confirmed, execute claim
      setConfirming(false);
      if (onClaim) {
        await onClaim(assignmentId);
      }
    } else {
      // Show confirmation
      setConfirming(true);
      // Auto-reset after 3 seconds
      setTimeout(() => setConfirming(false), 3000);
    }
  };

  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base',
  };

  const buttonStyle = confirming
    ? {
        backgroundColor: brand?.colors?.success || '#10B981',
        color: 'white',
      }
    : {
        backgroundColor: brand?.colors?.primary || '#2563EB',
        color: brand?.colors?.onPrimary || 'white',
      };

  return (
    <button
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={`
        rounded-lg font-medium transition-all
        ${sizeClasses[size]}
        ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}
      `}
      style={buttonStyle}
    >
      {isLoading ? (
        <span className="flex items-center gap-1.5">
          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Claiming...
        </span>
      ) : confirming ? (
        'Confirm Claim'
      ) : (
        'Claim'
      )}
    </button>
  );
};

interface ReleaseButtonProps {
  assignmentId: string;
  onRelease?: (assignmentId: string) => Promise<void> | void;
  disabled?: boolean;
  isLoading?: boolean;
  size?: ButtonSize;
  brand?: BrandConfig;
}

export const ReleaseButton: React.FC<ReleaseButtonProps> = ({
  assignmentId,
  onRelease,
  disabled = false,
  isLoading = false,
  size = 'md',
  brand,
}) => {
  const [confirming, setConfirming] = useState(false);

  const handleClick = async (): Promise<void> => {
    if (confirming) {
      setConfirming(false);
      if (onRelease) {
        await onRelease(assignmentId);
      }
    } else {
      setConfirming(true);
      setTimeout(() => setConfirming(false), 3000);
    }
  };

  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base',
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={`
        rounded-lg font-medium transition-all border
        ${sizeClasses[size]}
        ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}
      `}
      style={{
        borderColor: confirming ? (brand?.colors?.warning || '#F59E0B') : (brand?.colors?.border || '#E5E7EB'),
        color: confirming ? (brand?.colors?.warning || '#F59E0B') : (brand?.colors?.textLight || '#6B7280'),
        backgroundColor: confirming ? '#FEF3C7' : 'transparent',
      }}
    >
      {isLoading ? (
        <span className="flex items-center gap-1.5">
          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Releasing...
        </span>
      ) : confirming ? (
        'Confirm Release'
      ) : (
        'Release'
      )}
    </button>
  );
};

export default ClaimButton;
